import React, { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { LazyImg } from 'src/hooks/useLazyLoad';
import { Svg } from 'src/components/Svg/Svg';
import { Price } from 'src/components/Helpers/Price';
import { useVatCausesRender } from 'src/components/AppShell/VatProvider';
import { useProductListGtm } from 'src/components/CategoryListingPage/hooks/useProductListGtm';
import { ProductListItem } from 'src/components/CategoryListingPage/utils/productListItem';
import { Arrow } from 'src/elements/Carousel/settings';
import Slider from 'src/elements/Carousel/Slider';
import {
    recommendedProductList,
    recommendedProductListVariables,
} from 'src/queries/__generated__/recommendedProductList';
import getRecommendedProducts from 'src/queries/recommendedProductList.graphql';
import { useQuery } from '@apollo/client';
import { useDeps } from 'src/hooks/useDeps';
import classnames from 'classnames';

import { UpsellItem } from '../UpsellItem/UpsellItem';
import { ColorSwatchesProvider } from '../CategoryListingPage/ColorSwatchesProvider';

import classes from './FeaturedProducts.scss';

type Props = {
    listId: string;
    listName: string;
    bestSellers?: Set<number>;
    title?: string;
    skus: string[];
    twoRows?: boolean;
    tagsAggregation?: never[] | { label: string; value: string }[];
    isUpsells?: boolean;
    isSlider?: boolean;
    additionalSkuType?: 'screenPrintedSkus' | 'embroideredSkus';
    setAdditionalSkuType?: (type: 'screenPrintedSkus' | 'embroideredSkus') => void;
};

export const FeaturedProducts: React.FC<Props> = ({
    listId,
    listName,
    bestSellers,
    title,
    skus,
    twoRows = false,
    tagsAggregation,
    isUpsells = false,
    isSlider = false,
    additionalSkuType,
    setAdditionalSkuType,
}) => {
    const { data: currentData } = useQuery<recommendedProductList, recommendedProductListVariables>(
        getRecommendedProducts,
        {
            variables: {
                skus,
            },
            ssr: useDeps().env.SSR_GQL,
        },
    );

    // Keep showing the last loaded products (with the skus they were ordered by) while new
    // skus load, e.g. on the Printed/Embroidered toggle, instead of collapsing the section.
    const [loaded, setLoaded] = useState({ skus, data: currentData });
    if (currentData && currentData !== loaded.data) {
        setLoaded({ skus, data: currentData });
    }
    const data = currentData ?? loaded.data;
    const shownSkus = currentData ? skus : loaded.skus;

    const orderedProductList = data?.productList
        ? shownSkus
              .map((sku) => data.productList.find((product) => product?.sku?.toLowerCase() === sku.toLowerCase()))
              .filter((product): product is NonNullable<(typeof data.productList)[number]> => product != null)
        : undefined;

    const itemsCorrected = orderedProductList
        ? orderedProductList.map((el) => {
              return {
                  id: el?.id,
                  name: el?.name,
                  number_of_colors_available: el?.number_of_colors_available,
                  url_key: el?.url_key,
                  can_customise: el?.can_customise,
                  media_gallery: el?.media_gallery,
                  only_x_left_in_stock: el?.only_x_left_in_stock,
                  price: {
                      price: el?.price_range.minimum_price.regular_price_excl_formatted,
                      price_incl: el?.price_range.minimum_price.regular_price_formatted,
                  },
                  sku: el?.sku,
                  small_image: el?.small_image,
                  stock_qty: el?.stock_qty,
                  stock_status: el?.stock_status,
                  product_tags: el?.product_tags?.split(','),
                  configurable_options: el?.configurable_options,
                  variants: el?.variants,
              };
          })
        : ([] as any);

    const { vat } = useVatCausesRender();

    const { gtmSelectItem } = useProductListGtm({
        products: itemsCorrected,
        listName: listName,
        listId: listId,
    });

    const carouselSettings = useMemo(
        () => ({
            rows: twoRows ? 2 : 1,
            infinite: false,
            speed: 500,
            slidesToShow: 5,
            slidesToScroll: 1,
            initialSlide: 0,
            responsive: [
                {
                    breakpoint: 1024,
                    settings: {
                        slidesToShow: 4,
                        slidesToScroll: 1,
                    },
                },
                {
                    breakpoint: 600,
                    settings: {
                        slidesToShow: 3,
                        slidesToScroll: 1,
                    },
                },
                {
                    breakpoint: 480,
                    settings: {
                        slidesToShow: 2,
                        slidesToScroll: 1,
                    },
                },
            ],
            nextArrow: <Arrow direction="right" color="orange" />,
            prevArrow: <Arrow direction="left" />,
        }),
        [twoRows],
    );

    if (!data?.productList) return null;

    return (
        <div
            className={classnames(classes.productListContainer, {
                [classes.twoRows]: twoRows,
            })}
        >
            <div className={classes.productListHeader}>
                <h2>{title || 'Featured Products & Bestsellers'}</h2>
                {additionalSkuType && setAdditionalSkuType && (
                    <div className={classes.additionalSkuTypeContainer}>
                        <button
                            type="button"
                            className={classes.additionalSkuTypeButton}
                            onClick={() => setAdditionalSkuType('screenPrintedSkus')}
                        >
                            Screen Printed
                        </button>
                        <button
                            type="button"
                            className={classes.additionalSkuTypeButton}
                            onClick={() => setAdditionalSkuType('embroideredSkus')}
                        >
                            Embroidered
                        </button>
                        <button
                            type="button"
                            className={classes.additionalSkuTypeButtonMobile}
                            onClick={() => {
                                if (additionalSkuType === 'screenPrintedSkus') {
                                    setAdditionalSkuType('embroideredSkus');
                                } else {
                                    setAdditionalSkuType('screenPrintedSkus');
                                }
                            }}
                        >
                            <img src={require('../../svg/arrow-orange.svg')} alt="" />
                        </button>
                    </div>
                )}
            </div>
            {isUpsells ? (
                <ColorSwatchesProvider ids={itemsCorrected.map((el) => el.id)}>
                    <div className={classes.upsellContainer}>
                        {itemsCorrected.map((el, index) => (
                            <UpsellItem
                                product={el}
                                bestSellers={bestSellers}
                                gtmSelectItem={gtmSelectItem}
                                index={index}
                                key={el.sku}
                                tagsAggregation={tagsAggregation}
                            />
                        ))}
                    </div>
                </ColorSwatchesProvider>
            ) : isSlider ? (
                <Slider key={shownSkus.join('-')} {...carouselSettings}>
                    {itemsCorrected.map((el, index) => (
                        <div key={el.sku} className={classes.carouselElementContainer}>
                            <Item
                                product={el}
                                bestSellers={bestSellers}
                                gtmSelectItem={gtmSelectItem}
                                index={index}
                                vat={vat}
                                tagsAggregation={tagsAggregation}
                            />
                        </div>
                    ))}
                </Slider>
            ) : (
                <div className={classes.upsellContainer}>
                    {itemsCorrected.map((el, index) => (
                        <div key={el.sku} className={classes.carouselElementContainer}>
                            <Item
                                product={el}
                                bestSellers={bestSellers}
                                gtmSelectItem={gtmSelectItem}
                                index={index}
                                vat={vat}
                                tagsAggregation={tagsAggregation}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

type ItemProps = {
    product: ProductListItem;
    gtmSelectItem(index: number): void;
    index: number;
    vat: boolean;
    bestSellers?: Set<number>;
    tagsAggregation?: never[] | { label: string; value: string }[];
};

const Item: React.FC<ItemProps> = ({ product, gtmSelectItem, index, vat, bestSellers, tagsAggregation }) => {
    const isTaggedAsBestseller = product?.product_tags
        ?.map((pTag) => tagsAggregation?.find((tag) => tag.value === pTag)?.label)
        ?.includes('Bestseller');
    const displayBestsellerTag = (bestSellers && bestSellers.has(product.id)) || isTaggedAsBestseller;

    return (
        <div className={classes.productListItem} data-test-id="ProductItem">
            <div className={classes.productListContent}>
                <Link
                    to={`/${product.url_key || ''}`}
                    className={classes.productListImage}
                    onClick={() => gtmSelectItem(index)}
                >
                    {product.can_customise && (
                        <LazyImg
                            src={require('../../../static/img/addIcon.svg')}
                            alt="Add your logo"
                            className={classes.productListIcon}
                        />
                    )}
                    {product.is_24_hour_delivery && <Svg className={classes.productListDelivery} name="24hr" />}

                    {displayBestsellerTag && (
                        <small className={classes.productListBestseller}>
                            <Svg name="star" /> Bestseller
                        </small>
                    )}
                    <LazyImg src={product.small_image.url} alt={product.small_image.label} />
                </Link>
                <div className={classes.productListInfo}>
                    <p className={['h2', classes.productListTitle].join(' ')} title={product.name}>
                        <Link to={`/${product.url_key || ''}`} onClick={() => gtmSelectItem(index)}>
                            {product.name}
                        </Link>
                    </p>
                    <span className={classes.productListPrice}>
                        From{' '}
                        <b>
                            <Price price={product.price.price} priceIncl={product.price.price_incl} vat={vat} />
                        </b>{' '}
                        {vat ? 'inc' : 'ex'}. VAT
                    </span>
                    {product.number_of_colors_available && (
                        <small className={classes.productListMeta}>
                            <b>
                                {product.number_of_colors_available === 1
                                    ? `${product.number_of_colors_available} Colour`
                                    : `${product.number_of_colors_available} Colours`}{' '}
                            </b>{' '}
                            available
                        </small>
                    )}
                </div>
            </div>
        </div>
    );
};
